import type { Theme } from "@mui/material";
import { createTheme as createMuiTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import shadows from "@app/common/theme/shadows";
import commonTypography from "@app/common/theme/typography";

import breakpoints from "./breakpoints";
import components from "./components";
import corners from "./corners";
import palette from "./palette";
import sizes from "./sizes";
import spaces from "./spaces";
import typography, { defaults as typographyDefaults } from "./typography";

declare module "@mui/material" {
  export interface LinearProgressProps {
    barColor?: string;
  }
}

export const createTheme = (mode: "light" | "dark") => {
  const theme = createMuiTheme({
    shape: {
      borderRadius: 6,
    },
    shadows: shadows(mode),
    ...typographyDefaults,
    ...breakpoints(),
    corners,
    sizes,
    spaces,
  });

  // These are ordered to that
  // theme parts later in the list
  // have access to the earlier parts
  // ex. components can access the palette
  const themeParts: ((theme: Theme, mode: "light" | "dark") => object)[] = [
    palette,
    (theme: Theme) => ({
      typography: {
        ...commonTypography(theme).typography,
        ...typography(theme).typography,
      },
    }),
    () => ({ components }),
  ];

  return themeParts.reduce(
    (themeWhole, themePart) =>
      deepmerge(themeWhole, themePart(themeWhole, mode)),
    { ...theme }
  );
};

const defaultTheme = createTheme("light");

export default defaultTheme;
